import { authFetch } from '../lib/api.js';

/**
 * Generates a story based on the provided parameters
 * @param {Object} params - The story generation parameters
 * @param {Object} params.mainCharacter - The main character object
 * @param {Array} params.otherCharacters - Array of other character objects
 * @param {string} params.storytellingStyle - The style of storytelling
 * @param {Array} params.storyThemes - Array of story themes
 * @param {string} params.storySetting - The story setting
 * @param {string} params.storyDetails - Additional story details
 * @param {number} params.storyLength - The story length (0: Short, 1: Medium, 2: Long)
 * @param {boolean} params.generatePages - Whether to generate page-structured output
 * @param {string} params.authToken - Clerk auth token for API authentication
 * @returns {Promise<{title: string, content: string, pages?: Array}>} The generated story
 */
export const generateStory = async ({
  mainCharacter,
  otherCharacters,
  storytellingStyle,
  storyThemes,
  storySetting,
  storyDetails,
  storyLength,
  generatePages = true,
  authToken
}) => {
  if (!mainCharacter) {
    throw new Error('No main character provided');
  }

  const lengthText = valueToLengthText(storyLength);
  const lengthInstruction = getLengthInstruction(storyLength);
  const pageCount = getPageCount(storyLength);

  // Use page-structured prompt for visual storybook format
  const prompt = generatePages
    ? buildPageStructuredPrompt({
        mainCharacter,
        otherCharacters,
        storytellingStyle,
        storyThemes,
        storySetting,
        storyDetails,
        lengthText,
        lengthInstruction,
        pageCount
      })
    : buildTraditionalPrompt({
        mainCharacter,
        otherCharacters,
        storytellingStyle,
        storyThemes,
        storySetting,
        storyDetails,
        lengthText,
        lengthInstruction
      });

  try {
    const body = { prompt, maxTokens: 3000 };
    if (generatePages) {
      body.schema = buildPageStructuredSchema(pageCount);
    }

    const response = await authFetch('/api/generate-story', {
      method: 'POST',
      body: JSON.stringify(body),
    }, authToken);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (generatePages) {
      return parsePageStructuredResponse(data.story, mainCharacter, storySetting);
    }

    return parseStoryResponse(data.story);
  } catch (error) {
    throw new Error(`Failed to generate story: ${error.message}`);
  }
};

/**
 * JSON schema enforced by OpenAI structured outputs for the page-structured path.
 * Strict mode guarantees the response parses; the regex/markdown-stripping
 * fallback in parsePageStructuredResponse is now just a belt-and-braces safety net.
 */
const buildPageStructuredSchema = (pageCount) => ({
  type: 'object',
  additionalProperties: false,
  required: ['title', 'pages'],
  properties: {
    title: { type: 'string' },
    pages: {
      type: 'array',
      minItems: pageCount,
      maxItems: pageCount,
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['pageNumber', 'text', 'sceneDescription'],
        properties: {
          pageNumber: { type: 'integer' },
          text: { type: 'string' },
          sceneDescription: { type: 'string' },
        },
      },
    },
  },
});

/**
 * Build prompt for page-structured story output
 */
const buildPageStructuredPrompt = ({
  mainCharacter,
  otherCharacters,
  storytellingStyle,
  storyThemes,
  storySetting,
  storyDetails,
  lengthText,
  lengthInstruction,
  pageCount
}) => {
  return `Write a children's picture book story with exactly ${pageCount} pages.

Story details:
- Main character: ${formatCharacter(mainCharacter)}
- Other characters: ${formatCharacters(otherCharacters)}
- Storytelling style: ${storytellingStyle || "Normal"}
- Themes: ${storyThemes?.join(", ") || "None specified"}
- Setting: ${storySetting || "A magical place"}
- Additional details: ${storyDetails || "None"}
- Length guidance: ${lengthText}. ${lengthInstruction}

Story arc:
- Page 1 introduces the character and setting.
- Middle pages develop the adventure or problem.
- The final page resolves it warmly and positively.

Per-page guidance:
- text: 2-4 sentences suitable for reading aloud. If storytelling style is "Rhyming", make it rhyme.
- sceneDescription: a vivid illustration brief — character positions, expressions, actions, colors, lighting, mood, and background details. Keep it child-friendly.
- Number pages sequentially starting at 1.

Keep all content age-appropriate and positive.`;
};

/**
 * Build traditional (non-paginated) prompt
 */
const buildTraditionalPrompt = ({
  mainCharacter,
  otherCharacters,
  storytellingStyle,
  storyThemes,
  storySetting,
  storyDetails,
  lengthText,
  lengthInstruction
}) => {
  return `Generate a children's story based on the following details.
    **First, provide a suitable title for the story on its own line, prefixed with "Title:".**
    **Then, after a blank line, provide the full story content.**

    Details:
    Main character: ${formatCharacter(mainCharacter)}
    Other characters: ${formatCharacters(otherCharacters)}
    Storytelling style: ${storytellingStyle || "Normal"}
    Themes: ${storyThemes?.join(", ") || "None specified"}
    Setting: ${storySetting || "A familiar place"}
    Additional details: ${storyDetails || "None"}

    Story Length Guidance: ${lengthText}. ${lengthInstruction}

    Instructions:
    - Follow the output format: Title on the first line (prefixed with "Title:"), blank line, then the story.
    - The story should be engaging, age-appropriate, and incorporate the specified themes.
    - If the storytelling style is "Rhyming", ensure the story rhymes appropriately.
    - Ensure the story has a clear beginning, middle, and end, with character development and a satisfying resolution based on the length guidance.
    - Focus on creating a well-structured narrative according to the requested length.
    - Do NOT include the title again within the main body of the story content.`;
};

/**
 * Get page count based on story length
 */
const getPageCount = (storyLength) => {
  switch (storyLength) {
    case 0: return 4;  // Short
    case 2: return 10; // Long
    default: return 6; // Medium
  }
};

/**
 * Parse page-structured response from AI
 */
const parsePageStructuredResponse = (response, mainCharacter, setting) => {
  try {
    // Try to extract JSON from response
    let jsonStr = response;

    // Remove markdown code blocks if present
    const jsonMatch = response.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (jsonMatch) {
      jsonStr = jsonMatch[1];
    }

    // Try to find JSON object
    const objectMatch = jsonStr.match(/\{[\s\S]*\}/);
    if (objectMatch) {
      jsonStr = objectMatch[0];
    }

    const parsed = JSON.parse(jsonStr);

    if (!parsed.title || !parsed.pages || !Array.isArray(parsed.pages)) {
      throw new Error('Invalid story structure');
    }

    // Validate and clean pages
    const pages = parsed.pages.map((page, index) => ({
      pageNumber: page.pageNumber || index + 1,
      text: page.text || '',
      sceneDescription: page.sceneDescription || `Scene from page ${index + 1}`,
      imageUrl: null,
      imageStatus: 'pending',
    }));

    // Build full content for backwards compatibility
    const content = pages.map(p => p.text).join('\n\n');

    return {
      title: parsed.title,
      content,
      pages,
      mainCharacter: mainCharacter?.name || null,
      setting: setting || null,
    };
  } catch (error) {
    console.error('Failed to parse page-structured response:', error);
    // Fallback to traditional parsing
    const traditional = parseStoryResponse(response);
    return {
      ...traditional,
      pages: createPagesFromContent(traditional.content),
    };
  }
};

/**
 * Create pages from plain text content (fallback)
 */
const createPagesFromContent = (content) => {
  const paragraphs = content
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0);

  if (paragraphs.length === 0) return [];

  // Group into pages (2 paragraphs per page max)
  const pages = [];
  for (let i = 0; i < paragraphs.length; i += 2) {
    const pageText = paragraphs.slice(i, i + 2).join('\n\n');
    pages.push({
      pageNumber: pages.length + 1,
      text: pageText,
      sceneDescription: `Scene: ${pageText.substring(0, 150)}...`,
      imageUrl: null,
      imageStatus: 'pending',
    });
  }

  return pages;
};

// Helper functions
const valueToLengthText = (value) => {
  const lengths = ['Short', 'Medium', 'Long'];
  return lengths[value] || 'Medium';
};

const getLengthInstruction = (value) => {
  switch (value) {
    case 0: return "Keep the plot concise and focused, aiming for roughly 3-5 paragraphs.";
    case 2: return "Expand on the plot with more descriptive details and character development, aiming for 10+ paragraphs.";
    default: return "Develop the plot with a clear beginning, rising action, climax, and resolution, aiming for roughly 6-9 paragraphs.";
  }
};

const formatCharacter = (character) => {
  if (!character) return "Unknown";
  
  const traits = ensureArray(character.traits);
  const abilities = ensureArray(character.specialAbility);
  const likes = ensureArray(character.likes);
  const dislikes = ensureArray(character.dislikes);
  const fears = ensureArray(character.fears);
  const dreams = ensureArray(character.dreams);
  
  let description = `${character.name || "Unknown"} (${character.gender || "?"}, ${character.age || "?"} years old)`;
  
  if (traits.length > 0) description += `, traits: ${traits.join(", ")}`;
  if (abilities.length > 0) description += `, special ability: ${abilities.join(", ")}`;
  if (likes.length > 0) description += `, likes: ${likes.join(", ")}`;
  if (dislikes.length > 0) description += `, dislikes: ${dislikes.join(", ")}`;
  if (fears.length > 0) description += `, fears: ${fears.join(", ")}`;
  if (dreams.length > 0) description += `, dreams: ${dreams.join(", ")}`;
  
  return description;
};

const formatCharacters = (characters) => {
  if (!characters?.length) return "None";
  return characters.map(formatCharacter).join("; ");
};

const parseStoryResponse = (response) => {
  const titleMatch = response.match(/^Title:(.*?)(\n\n|$)/i);
  let title = "Untitled Story";
  let content = response;
  
  if (titleMatch && titleMatch[1]) {
    title = titleMatch[1].trim();
    content = response.substring(titleMatch[0].length).trim();
  }

  if (!title || !content) {
    throw new Error("Generated response missing title or story content");
  }

  return { title, content };
};

const ensureArray = (value) => {
  if (Array.isArray(value)) {
    return value.map(item => String(item));
  }
  if (typeof value === 'string') {
    return [value];
  }
  return [];
};
